/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import 'dayjs/locale/lt';

import IconConfig from '@assets/icons/config.svg?react';
import {
 Autocomplete,
 Box,
 Button,
 IconButton,
 TextField,
 Typography,
 useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
// eslint-disable-next-line unused-imports/no-unused-imports
import isoWeek from 'dayjs/plugin/isoWeek';
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ColPresetType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { StructureType } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import SelectApi from '../SelectApi';
import { CustomGridApi } from '../XTable/types';
import DateRangeFilter from './DateRangeFilter';
import OperatorMenu from './OperatorMenu';
import { DateRangeColumn } from './types';
// for start/end of week
dayjs.extend(isoWeek);

interface FilterFieldsProps {
 apiRef: MutableRefObject<CustomGridApi>;
 applyFilters: (data: FormValues) => void;
 resetFilters: () => void;
 setColModalOpen: Dispatch<SetStateAction<boolean>>;
 columns: GridColDef[];
 dateRangeColumns: DateRangeColumn[];
 currentColumnsPreset: ColPresetType | null;
 structure: StructureType[];
 isFormChanged: boolean;
}

interface FormValues {
 [key: string]: any;
}

const FilterFields = ({
 apiRef,
 applyFilters,
 resetFilters,
 setColModalOpen,
 columns,
 dateRangeColumns,
 currentColumnsPreset,
 structure,
 isFormChanged,
}: FilterFieldsProps) => {
 const { t } = useTranslation();
 const { handleSubmit, control, formState, setValue, getValues } =
  useFormContext();
 const th = useTheme();

 const noFilters = !Object.keys(
  currentColumnsPreset?.filters?.fields || {},
 ).filter((key) => findStructure(structure, key)?.Type !== 'datetime').length;

 return (
  columns && (
   <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
    <Box
     // noValidate
     sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
     }}
    >
     <Box sx={{ overflowY: 'auto', pb: 1, flex: 1 }}>
      {dateRangeColumns.length > 0 && (
       <DateRangeFilter columns={dateRangeColumns} />
      )}
      <Box
       sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
       }}
      >
       <Typography
        sx={{
         color: th.palette.kemek.darkGray,
         fontSize: 14,
         fontWeight: 500,
         lineHeight: 'initial',
        }}
       >
        {t('global.chosenFilters')}
       </Typography>
       <IconButton
        onClick={() => setColModalOpen(true)}
        sx={{ width: '24px', height: '24px', p: 0 }}
       >
        <IconConfig
         style={{
          width: 18,
          height: 18,
          color: th.palette.kemek.darkestGray,
         }}
        />
       </IconButton>
      </Box>
      <Box
       sx={{
        flex: 1,
        // overflowY: 'auto',
        pt: '12px', // for not overflowing with label
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
       }}
      >
       {noFilters && (
        <Box
         sx={{
          textAlign: 'center',
          p: '12px',
          backgroundColor: th.palette.kemek.lightBlue,
          mb: 3,
         }}
        >
         <Typography variant="body2" color="text.secondary">
          {t('global.noItemsFound')}
         </Typography>
        </Box>
       )}
       {Object.keys(currentColumnsPreset?.filters?.fields || {}).map(
        (fieldKey: string) => {
         const exists = true; // columnExists(columns, fieldKey);
         const currentStructure = findStructure(structure, fieldKey);
         if (
          ['DateFrom', 'DateTo'].includes(fieldKey) ||
          !currentStructure ||
          currentStructure?.Type === 'datetime'
         )
          return null;

         let returnObj;

         switch (currentStructure?.Type) {
          case 'model':
           returnObj = (
            <SelectApi
             name={fieldKey}
             url={currentStructure.ForeignKeyUrl}
             filter={currentStructure.ForeignKeyDefaultFilter}
             label={getTranslation(structure, fieldKey)}
             isEditable
             operator="contains"
             control={control}
             setValue={setValue}
             multiple
            />
           );
           break;
          case 'array':
           returnObj = (
            <SelectApi
             name={fieldKey}
             url={currentStructure.ForeignKeyUrl}
             filter={currentStructure.ForeignKeyDefaultFilter}
             label={getTranslation(structure, fieldKey)}
             isEditable
             operator="contains"
             control={control}
             setValue={setValue}
             multiple
            />
           );
           break;
          case 'enum':
           returnObj = (
            <Controller
             name={fieldKey}
             control={control}
             defaultValue="" // Default value for controlled input
             render={({ field }) => (
              <Autocomplete
               {...field}
               multiple
               value={field.value || []}
               onChange={(_, value: any) => {
                console.log('onChange', value, _);
                setValue(fieldKey, value || '');
               }}
               options={Object.entries(currentStructure.EnumValues).map(
                ([value, label]) => ({
                 Id: value,
                 Name: label,
                }),
               )}
               renderOption={(props, option) => (
                <li
                 {...props}
                 key={`${fieldKey}-${option[fieldKey]}-${option.Id}`}
                >
                 {option.Name}
                </li>
               )}
               getOptionLabel={(option: any) =>
                (currentStructure.EnumValues[option] || option.Name).toString()
               }
               isOptionEqualToValue={(option, value) =>
                (option as any).Id === value.Id
               }
               renderInput={(params) => (
                <TextField
                 {...params}
                 label={currentStructure.Translation}
                 variant="outlined"
                />
               )}
               sx={{ width: '100%' }}
              />
             )}
            />
           );
           break;
          default:
           returnObj = (
            <Controller
             name={fieldKey}
             control={control}
             render={({ field }) => (
              <TextField
               {...field}
               value={field.value || ''}
               onChange={(e) => {
                setValue(fieldKey, e.target.value || '');
               }}
               label={getTranslation(structure, fieldKey)}
               variant="outlined"
               error={!!formState?.errors?.[fieldKey]}
               fullWidth
              />
             )}
            />
           );
           break;
         }

         return returnObj ? (
          <Box
           sx={{
            p: 0, // prevents overflow scroll for some reason, fix
            width: '100%',
            display: 'flex',
            gap: 1,
            alignItems: 'center',
           }}
           key={fieldKey}
          >
           <Box sx={{ flex: 1, maxWidth: 234 }}>{returnObj}</Box>
           <Controller
            name={`operators[${fieldKey}]`}
            control={control}
            defaultValue=""
            //  rules={{ required: !exists ? false : 'Operator is required' }}
            render={({ field }) => (
             <>
              <TextField
               {...field}
               type="hidden"
               variant="standard"
               sx={{ display: 'none' }}
              />
              <OperatorMenu
               operators={[
                ...(columns.find((item) => item.field === fieldKey)
                 ?.filterOperators || []),
               ]}
               filter={fieldKey}
               columnExists={exists}
               value={field.value}
               structure={currentStructure}
              />
             </>
            )}
           />
          </Box>
         ) : null;
        },
       )}
      </Box>
     </Box>
    </Box>
   </LocalizationProvider>
  )
 );
};
FilterFields.displayName = 'FilterFields';
export default FilterFields;
